import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ExchangeRateContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export function ExchangeRateProvider({ children }) {
    const [rates, setRates] = useState({
        USD: 1,
        TRY: 0,
        EUR: 0,
        GBP: 0
    });
    const [baseCurrency, setBaseCurrency] = useState('USD');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchRates = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/market/exchange-rates?from=USD&to=TRY,EUR,GBP`);

            if (!response.ok) {
                throw new Error('Failed to fetch exchange rates');
            }

            const data = await response.json();

            setRates({
                USD: 1,
                TRY: data.rates.TRY || 0,
                EUR: data.rates.EUR || 0,
                GBP: data.rates.GBP || 0
            });
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Exchange rate fetch error:', err);
            setError(err.message);
            // Fallback rates if API fails
            setRates({
                USD: 1,
                TRY: 34.5,
                EUR: 0.92,
                GBP: 0.79
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRates();

        // Refresh rates every 30 minutes
        const interval = setInterval(fetchRates, 30 * 60 * 1000);

        return () => clearInterval(interval);
    }, [fetchRates]);

    const convert = useCallback((amount, from, to) => {
        if (from === to) return amount;

        // Convert to USD first, then to target currency
        let inUSD = amount;
        if (from !== 'USD') {
            inUSD = amount / rates[from];
        }

        if (to === 'USD') {
            return inUSD;
        }

        return inUSD * rates[to];
    }, [rates]);

    const formatCurrency = useCallback((amount, currency = 'USD') => {
        const localeMap = {
            USD: 'en-US',
            TRY: 'tr-TR',
            EUR: 'de-DE',
            GBP: 'en-GB'
        };

        return new Intl.NumberFormat(localeMap[currency] || 'en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    }, []);

    const getUsdToTry = useCallback(() => {
        return rates.TRY;
    }, [rates]);

    const value = {
        rates,
        baseCurrency,
        setBaseCurrency,
        loading,
        error,
        lastUpdated,
        convert,
        formatCurrency,
        getUsdToTry,
        refreshRates: fetchRates
    };

    return (
        <ExchangeRateContext.Provider value={value}>
            {children}
        </ExchangeRateContext.Provider>
    );
}

export function useExchangeRate() {
    const context = useContext(ExchangeRateContext);
    if (!context) {
        throw new Error('useExchangeRate must be used within an ExchangeRateProvider');
    }
    return context;
}
